import { validateSync } from 'class-validator';
import { plainToInstance } from 'class-transformer';
import { RgbaDto } from '../RgbaDto';
import { GradientDto } from '../GradientDto';

/**
 * Shared validation logic for Gradient objects only.
 * Returns true if the value is a valid Gradient object.
 */
export function validateGradient(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const gradientInstance = plainToInstance(GradientDto, value, {
    enableImplicitConversion: true,
  });

  const gradientErrors = validateSync(gradientInstance, {
    skipMissingProperties: false,
    whitelist: false,
    forbidNonWhitelisted: false,
  });

  return gradientErrors.length === 0;
}

/**
 * Shared validation logic for RGBA and Gradient objects.
 * Returns true if the value is a valid RGBA or Gradient object.
 */
export function validateRgbaOrGradient(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  // Validate as RGBA or Gradient using DTOs
  // Use enableImplicitConversion to ensure nested objects are properly transformed
  const rgbaInstance = plainToInstance(RgbaDto, value, {
    enableImplicitConversion: true,
  });
  const gradientInstance = plainToInstance(GradientDto, value, {
    enableImplicitConversion: true,
  });

  // Validate both - at least one must pass
  const rgbaErrors = validateSync(rgbaInstance, {
    skipMissingProperties: false,
    whitelist: false,
    forbidNonWhitelisted: false,
  });

  // For gradients, use skipMissingProperties: false to enforce required fields (type, stops)
  // Optional fields (angle, centerPoint, shapeCoords) are marked with @IsOptional
  const gradientErrors = validateSync(gradientInstance, {
    skipMissingProperties: false,
    whitelist: false,
    forbidNonWhitelisted: false,
  });

  return rgbaErrors.length === 0 || gradientErrors.length === 0;
}
